// pages/ForgotPassword.tsx
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, Link } from "react-router-dom";
import {
  Mail,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Loader2,
  AlertCircle,
  KeyRound,
} from "lucide-react";
import { useForgotPassword } from "../../lib/api/authOnboarding";
import logo from "../../assets/logo.png";
import signinImg from "../../assets/signin.png";

const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});
const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL;

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPassword() {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const { mutateAsync: forgotPassword, isPending } = useForgotPassword();
  const [mailSuccess, setMailSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onSubmit = async (data: ForgotPasswordForm) => {
    setErrorMessage(null);
    try {
      const res = await forgotPassword({ email: data.email });
      console.log("Reset link response:", res);
      setMailSuccess(true);
    } catch (error: any) {
      console.warn(error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to send reset link. Please try again."
      );
    }
  };

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left Side - Image (Fixed) */}
      <div className="hidden lg:block lg:w-1/2 xl:w-[55%] relative h-screen">
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#8B52FF]/90 to-[#E05A6A]/90 z-10" />

        {/* Image */}
        <img
          src={signinImg}
          alt="Reset Password"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Content Overlay */}
        <div className="absolute inset-0 z-20 flex flex-col justify-between p-12 xl:p-16">
          <div></div>
          <div className="max-w-lg">
            <h2 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
              Reset Your Password
            </h2>
            <p className="text-lg text-white/80 leading-relaxed">
              Don't worry, it happens to the best of us. We'll help you get back
              into your account.
            </p>
          </div>
          <div className="flex items-center gap-8">
            <div>
              <div className="text-3xl font-bold text-white">50K+</div>
              <div className="text-white/70 text-sm">Contributors</div>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div>
              <div className="text-3xl font-bold text-white">10+</div>
              <div className="text-white/70 text-sm">Countries</div>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div>
              <div className="text-3xl font-bold text-white">$1M+</div>
              <div className="text-white/70 text-sm">Paid Out</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Content */}
      <div className="flex-1 flex flex-col bg-white h-screen">
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-6 lg:p-8 border-b border-gray-100">
          <Link to={FRONTEND_URL as string}>
            <img src={logo} alt="Unimerg" className="h-9 w-auto" />
          </Link>
        </div>

        {/* Content Container */}
        <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
          <div className="flex-1 flex items-center justify-center px-6 lg:px-12 xl:px-20 py-8">
            <div className="w-full max-w-sm">
              {mailSuccess ? (
                /* Success View */
                <div className="text-center">
                  {/* Success Icon */}
                  <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-lg shadow-green-500/30 mb-8">
                    <CheckCircle className="w-10 h-10 text-white" />
                  </div>
                  {/* Header */}
                  <h1 className="text-3xl font-bold text-gray-900 mb-3">
                    Check Your Email
                  </h1>
                  <p className="text-gray-500 leading-relaxed mb-2">
                    We've sent a password reset link to
                  </p>
                  <p className="text-[#8B52FF] font-semibold mb-6">
                    {watch("email")}
                  </p>
                  <p className="text-gray-500 text-sm leading-relaxed mb-8">
                    Click the link in the email to reset your password. If you
                    don't see it, check your spam folder.
                  </p>
                  {/* Back to Login Button */}
                  <button
                    onClick={() => navigate("/login")}
                    className="w-full py-3.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-[#8B52FF]/25 hover:shadow-xl hover:shadow-[#8B52FF]/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 flex items-center justify-center gap-2 group"
                  >
                    Back to Login
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                  </button>
                </div>
              ) : (
                /* Form View */
                <div>
                  {/* Icon */}
                  <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-[#8B52FF] to-[#E05A6A] flex items-center justify-center shadow-lg shadow-[#8B52FF]/30 mb-8">
                    <KeyRound className="w-10 h-10 text-white" />
                  </div>
                  {/* Header */}
                  <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-3">
                      Forgot Password?
                    </h1>
                    <p className="text-gray-500 leading-relaxed">
                      No worries! Enter your email and we'll send you a reset
                      link.
                    </p>
                  </div>
                  {/* Error Message */}
                  {errorMessage && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-red-600 text-sm">{errorMessage}</p>
                    </div>
                  )}
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Email Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="email"
                          {...register("email")}
                          placeholder="you@example.com"
                          className={`w-full pl-12 pr-4 py-3.5 rounded-xl border-2
                            ${
                              errors.email
                                ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                                : "border-gray-200 focus:border-[#8B52FF] focus:ring-[#8B52FF]/20"
                            }
                            bg-white text-gray-900 placeholder-gray-400
                            focus:outline-none focus:ring-4
                            transition-all duration-200`}
                        />
                      </div>
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.email.message}
                        </p>
                      )}
                    </div>
                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isPending}
                      className="w-full py-3.5 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white font-semibold rounded-xl shadow-lg shadow-[#8B52FF]/25 hover:shadow-xl hover:shadow-[#8B52FF]/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-lg"
                    >
                      {isPending ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Sending Reset Link...
                        </>
                      ) : (
                        <>
                          Send Reset Link
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                        </>
                      )}
                    </button>
                  </form>
                  {/* Back to Login Link */}
                  <div className="mt-8 text-center">
                    <Link
                      to="/login"
                      className="inline-flex items-center gap-2 text-gray-500 hover:text-[#8B52FF] font-medium transition-colors"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to Login
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
